<template>
  <div
      id="wrapper"
      :class="{'is-active': !learningStore.isDarkMode}"
      :style="wrapStyle"
      @mousemove="interactiveMethods.onMousemove"
      @mousedown="interactiveMethods.onMousemove"
      @mouseup="interactiveMethods.onMouseup"
      @touchstart="interactiveMethods.onTouchMove"
      @touchmove="interactiveMethods.onTouchMove"
      @touchend="interactiveMethods.onTouchEnd"
  >
    <LazyGlobalLoading v-if="loadingStore.show"/>
    <div v-else class="lectureHeader">
      <div class="skillProgress">
        <div class="titleArea">
          <div class="leftArea">
            <div class="logoIcon"><img src="~/assets/images/logo_mark.svg" alt="logo_mark"></div>
            <h3>codepresso</h3>
          </div>
          <div class="rightArea">
            <div class="labelWrap">
              <div class="timeBox"><h3>{{ learningTimeText }}</h3></div>
              <div v-if="learningStore.task.level > 0" class="difficultyBox" :class="levelClass">{{ levelText }}</div>
              <div class="policyHeader">
                <div><h3>{{ t('home-event-info-language') }}</h3></div>
                <div class="langSelect" @click.stop="openLangToggle = !openLangToggle">
                  <div class="downToggle" :class="{ on: openLangToggle }">
                    <div class="icon"></div>
                  </div>
                  <ul class="scrollY" :class="{ on: openLangToggle }">
                    <li v-for="(lang, index) of keysOfLanguages" :key="index" :class="{ active: $i18n.locale.value === lang }" @click.stop="changeLocale(lang)">
                      {{ LocalLanguagesType[lang] }}
                    </li>
                  </ul>
                </div>
                <LazyMultiFileAllReset v-if="learningStore.task.type === TaskType.MULTI_FILE_CODE && learningStore.directory.isInitDirectory" />
              </div>
            </div>
          </div>
          <div>
            <button class="markdown-upload-btn" @click="onMarkdownUploadClick">
              마크다운 파일 검증
            </button>
            <input ref="markdownFileInput" type="file" accept=".md" style="display: none;" @change="onMarkdownFileChange" />
          </div>
        </div>
      </div>
    </div>
    <LazyLearningMiddle ref="middle" />
    <LazyLearningFooter />
  </div>
</template>

<script setup lang="ts">
import {LazyGlobalLoading, LazyLearningFooter, LazyLearningMiddle, LazyMultiFileAllReset} from "#components";
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  totalLearningTimeConvert,
  useI18n,
  useRoute,
  useRouter,
  watch
} from "#imports";
import {useNuxtApp} from "#app";
import {useLearningStore} from "~/store/learning/learningStore";
import {onBeforeRouteLeave} from "vue-router";
import {useUserStore} from "~/store/user/userStore";
import {useDialogStore} from "~/store/dialog/dialogStore";
import {useLearningService} from "~/composables/service/useLearningService";
import {LevelType, LevelTypeText} from "~~/package/mapper/learning/model/LevelType";
import LocalLanguagesType from "~~/package/enum/LocalLanguagesType";
import {useUserService} from "~/composables/service/useUserService";
import {TaskType} from "~~/package/mapper/learning/model/TaskType";
import {useGenericDialog} from "~/composables/useGenericDialog";
import {useLearningWindowEventService} from "~/composables/service/useLearningWindowEventService";
import {useLoadingStore} from "~/store/loading/loadingStore";
import {TaskModel} from "~~/package/mapper/learning/model/TaskModel";
import type {CodeOrderModel} from "~~/package/mapper/learning/model/CodeOrderModel";
import {CodeHintModel} from "~~/package/mapper/learning/model/CodeHintModel";
import {CodeQuestionModel} from "~~/package/mapper/learning/model/CodeQuestionModel";
import {CodeInputModel} from "~~/package/mapper/learning/model/CodeInputModel";
import {CodeSelectionModel} from "~~/package/mapper/learning/model/CodeSelectionModel";

const { t } = useI18n();
const learningStore = useLearningStore();
const dialogStore = useDialogStore();
const userStore = useUserStore();
const { openWrongApproachAlert } = useGenericDialog();
const loadingStore = useLoadingStore();
const learningWindowEventService = useLearningWindowEventService();
const router = useRouter();
const route = useRoute();
const middle = ref();
const openLangToggle = ref(false);

const base64Content = ref<string | null>(null);
const markdownFileInput = ref<HTMLInputElement | null>(null);

const {$i18n} = useNuxtApp();
const lang = computed(() =>
    $i18n.locale.value.toUpperCase()
);

const keysOfLanguages = computed((): Array<string> => {
  return Object.keys(LocalLanguagesType);
});

const levelClass = computed(() => {
  return {[`${LevelTypeText[LevelType[learningStore.task.level]]}`]: true}
});
const levelText = computed(() => {
  return t(`learning-${LevelTypeText[LevelType[learningStore.task.level]]}`);
});
const learningTimeText = computed(() => totalLearningTimeConvert(learningStore.task.learningTime as number, t('common-hour'), t('common-min')));

onBeforeRouteLeave((to, from, next) => {
  learningStore.isValidate = false;
  learningStore.resetAll();
  next();
});

onBeforeMount(() => {
  loadingStore.openLoading("");
  learningStore.resetAssessment();
  learningStore.resetContestData();
  learningStore.resetLevelAssessment();
  learningStore.resetProSkillAssure();
  learningWindowEventService.addEventListener(false);
});

onMounted(() => {
  learningStore.isNeverRun = true;
  if (userStore.isLogin()) {
    learningStore.resetInitData();
    learningStore.isValidate = true;
    initTask();
  }
  else goToHomePage();
});

onBeforeUnmount(() => {
  learningWindowEventService.removeEventListener(false);
});

const initTask = async () => {
  await useLearningService().getTaskForValidate(route.params.uuid as string)
      .then(task => {
        learningStore.updateLearningData(task);
        useLearningService().getTaskAnswer(task.id)
            .then(taskAnswer => {
              learningStore.taskAnswer = taskAnswer;
              loadingStore.closeLoading();
            }).catch(() => openFailPopup());
      }).catch(() => openFailPopup());
};

const openFailPopup = () => {
  openWrongApproachAlert();
};

const goToHomePage = () => {
  router.push({path: "/"});
};

const interactiveMethods = {
  onMousemove(event: MouseEvent) {
    if (learningStore.draggingActivityWidget.id !== -1 || learningStore.isChangeActivityMenuHeight) {
      learningStore.mousePosition = {x: event.clientX, y: event.clientY}
      interactiveMethods.setContentPosition();
    }
  },
  onMouseup(event: MouseEvent) {
    learningStore.isChangeActivityMenuHeight = false;
    if (learningStore.draggingActivityWidget.id !== -1) {
      const isInteractionWidget = (event.target as HTMLElement).getAttribute("is-interaction-widget")
      if (
          learningStore.draggingActivityWidget.isOrderWidget
          && learningStore.draggingActivityWidget.isSelectedCard
          && !isInteractionWidget
      ) {
        const index = learningStore.task.markOrderList?.findIndex(order => order.id === learningStore.draggingActivityWidget.id)
        learningStore.task.markOrderList[index].answerIndex = -1

        learningStore.isOrderMenuOpen = true;
        learningStore.isSelectedSelectionId = -1;
      }

      learningStore.draggingActivityWidget = {
        isOrderWidget: false,
        isSelectedCard: false,
        id: -1,
      }
      learningStore.mousePosition = {x: -1, y: -1}
    }
  },
  onTouchMove(event: TouchEvent) {
    if(learningStore.isChangeActivityMenuHeight) {
      if(event.touches && event.touches.length > 0) {
        learningStore.mousePosition = {x: event.touches[0].clientX, y: event.touches[0].clientY}
      }
      interactiveMethods.setContentPosition();
    }
  },
  onTouchEnd() {
    learningStore.isChangeActivityMenuHeight = false;
  },
  setContentPosition() {
    if (learningStore.isChangeActivityMenuHeight) {
      learningStore.contentPosition = {
        height: middle.value.$el.clientHeight,
        top: middle.value.$el.getBoundingClientRect().top
      }
    }
  }
}

const wrapStyle = computed(() => {
  if (learningStore.draggingActivityWidget.id !== -1) {
    return {
      userSelect: 'none',
      cursor: 'grabbing'
    }
  }
  return {}
});

const changeLocale = (locale: string) => {
  useUserService().setUserLocale(locale);
  window.scrollTo(0, 0);
  $i18n.setLocale(locale);
  openLangToggle.value = false;
};


const onMarkdownUploadClick = () => {
  markdownFileInput.value?.click()
}

const onMarkdownFileChange = async (event: Event) => {
  learningStore.resetInitData();
  learningStore.resetAssessment();
  learningStore.resetContestData();
  learningStore.resetLevelAssessment();
  learningStore.resetProSkillAssure();

  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = async () => {
    const result = reader.result as string
    base64Content.value = result.split(',')[1]

    loadingStore.openLoading("");

    try {
      const res = await fetch("https://lldqpuimtk.execute-api.ap-northeast-2.amazonaws.com/md2json", {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: base64Content.value,
      })
      const json = await res.json();
      const task = {...json.data,};

      if(task.hintList) task.hintList = attachId<CodeHintModel>(json.data.hintList);
      if(task.markQuestionList) task.markQuestionList = attachId<CodeQuestionModel>(json.data.markQuestionList);
      if(task.markDictationList) task.markDictationList = attachId<CodeInputModel>(json.data.markDictationList);
      if(task.markSelectionList) task.markSelectionList = attachId<CodeSelectionModel>(json.data.markSelectionList);
      if(task.markOrderList) task.markOrderList = attachId<CodeOrderModel>(json.data.markOrderList);
      if(task.multipleChoiceQuestion) task.multipleChoiceQuestion = {
        ...task.multipleChoiceQuestion,
        multipleChoiceResult: {
          answerList: []
        }
      }


      learningStore.updateLearningData(TaskModel.create(task));
      setTimeout(() => {
        loadingStore.closeLoading();
      }, 500);
    } catch (e) {
      console.error(e);
    }
  }
  reader.readAsDataURL(file)
}

const attachId = <T>(list: T[] | undefined): (T & { id: number })[] => {
  return (list ?? []).map((item, index) => ({ ...item, id: index }));
};

watch($i18n.locale, () => {
  initTask();
});
</script>

<style lang="scss">
@import "~/assets/scss/abstracts/variables.scss";
@import "~/assets/scss/abstracts/mixin.scss";
@import "~/assets/scss/policy.scss";

.markdown-upload-btn {
  background-color: #325FD0;
  color: #E7E9EF;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: darken(#325FD0, 5%);
  }
}
</style>
